import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrencyRates } from '@magimp/shared-hooks';
import { Button } from '../../../../ui/index.js';
import { useAuthStore } from '../../../../stores/auth.js';
import { useRequestsStore } from '../../store.js';
import { canPickWinner } from '../../role.js';
import { aggregateProductQuantity, getCheapestBid } from '../../pricing.js';
import type { CarrierBid, CarrierBidQuote, RequestView, UserRef } from '../../types.js';
import { CarriersBidFormModal } from './CarriersBidFormModal.js';
import { CarriersDestinationSection } from './CarriersDestinationSection.js';
import { CarriersGeneralSection } from './CarriersGeneralSection.js';
import { CarriersRejectModal } from './CarriersRejectModal.js';

export interface CarriersTabProps {
  request: RequestView;
  me: UserRef;
}

type CarrierModal =
  | { type: 'addBid'; bid: CarrierBid }
  | { type: 'editBid'; bid: CarrierBid; quote: CarrierBidQuote }
  | { type: 'reject'; bid: CarrierBid }
  | null;

export function CarriersTab({ request: r }: CarriersTabProps) {
  const { t } = useTranslation(['requests', 'common']);
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  const setModal = useRequestsStore((s) => s.setModal);
  const [rowModal, setRowModal] = useState<CarrierModal>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const party = {
    actorUserId: user?.id ?? null,
    managerId: r.managerId,
    logistId: r.logistId,
  };
  const showCTA = canPickWinner(permissions, r.status, r.winnerBidId != null, party);
  const answered = r.carriers.filter((c) => c.state === 'answered');
  // WHY (#1058): "completed response" is ANSWERED + REJECTED. The header
  //   counter reports completion progress so a request with rejections
  //   tells an honest story; `answered` stays the cheapest-bid input
  //   (rejected carriers carry no quote).
  const completedCount = r.carriers.filter(
    (c) => c.state === 'answered' || c.state === 'rejected',
  ).length;
  const { data: fx } = useCurrencyRates();
  // WHY (#1060): piece-unit bids (#1056 SKU count) need an aggregate
  //   productQuantity basis so the FX ranker can compare them against
  //   kg/m3 bids. Null when any item lacks the field — see pricing.ts.
  const requestProductQuantity = aggregateProductQuantity(r.items);
  const cheapestBidId =
    getCheapestBid(
      answered,
      { weight: r.weight, volume: r.volume, productQuantity: requestProductQuantity },
      fx?.rates ?? {},
    )?.bid.id ?? null;
  const fxRates = fx?.rates ?? {};

  const showLeftSegment = answered.length > 0;
  const showHeaderBar = showLeftSegment || showCTA;

  return (
    <div style={{ padding: '12px 24px 24px' }}>
      {showHeaderBar && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 12,
            flexWrap: 'wrap',
          }}
        >
          {showLeftSegment && (
            <>
              <span
                style={{
                  fontSize: 10,
                  letterSpacing: '.1em',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  color: 'var(--fg-3)',
                }}
              >
                {t('carriers.bestOffers')}
              </span>
              <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                {t('carriers.completedCount', {
                  completed: completedCount,
                  total: r.carriers.length,
                })}
              </span>
            </>
          )}
          {showCTA && (
            <Button
              kind="primary"
              size="sm"
              icon="crown"
              onClick={() => setModal({ type: 'pickWinner', requestId: r.id })}
              style={{ marginLeft: 'auto' }}
            >
              {t('carriers.pickWinner')}
            </Button>
          )}
        </div>
      )}

      {actionError && (
        <div style={{ color: 'var(--status-error-fg)', fontSize: 12, marginBottom: 8 }}>
          {actionError}
        </div>
      )}

      <div style={{ display: 'grid', gap: 18 }}>
        {/* WHY (#1057): CONTAINER requests render one general carriers
         *   group (request-wide scope); STANDARD requests keep the
         *   per-destination sections. The wire's `arrivalCityId` is
         *   `null` on every CONTAINER carrier row and a UUID on every
         *   STANDARD row, so the two branches never overlap. */}
        {r.kind === 'CONTAINER' ? (
          <CarriersGeneralSection
            request={r}
            cheapestBidId={cheapestBidId}
            fxRates={fxRates}
            onAddBid={(bid) => setRowModal({ type: 'addBid', bid })}
            onEditBid={(bid, quote) => setRowModal({ type: 'editBid', bid, quote })}
            onReject={(bid) => setRowModal({ type: 'reject', bid })}
            onError={setActionError}
          />
        ) : (
          r.destinations.map((d) => (
            <CarriersDestinationSection
              key={d.arrivalCity.id}
              request={r}
              destination={d}
              cheapestBidId={cheapestBidId}
              fxRates={fxRates}
              onAddBid={(bid) => setRowModal({ type: 'addBid', bid })}
              onEditBid={(bid, quote) => setRowModal({ type: 'editBid', bid, quote })}
              onReject={(bid) => setRowModal({ type: 'reject', bid })}
              onError={setActionError}
            />
          ))
        )}
      </div>

      {rowModal?.type === 'addBid' && (
        <CarriersBidFormModal
          requestId={r.id}
          bid={rowModal.bid}
          quote={null}
          onClose={() => setRowModal(null)}
        />
      )}
      {rowModal?.type === 'editBid' && (
        <CarriersBidFormModal
          requestId={r.id}
          bid={rowModal.bid}
          quote={rowModal.quote}
          onClose={() => setRowModal(null)}
        />
      )}
      {rowModal?.type === 'reject' && (
        <CarriersRejectModal
          requestId={r.id}
          bid={rowModal.bid}
          onClose={() => setRowModal(null)}
        />
      )}
    </div>
  );
}
